import React, { FC, useEffect, useState } from 'react';
import { Alert, Button, Form, Input } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { rules } from 'common/helpers/form.helper';
import { EFormFieldMessage } from 'common/const/form.enum';
import { normalizePhone } from 'common/helpers';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { IUpdateProfilePayload } from 'entities/SelfRegistration/SelfRegistration.models';
import { getCurrentUser, selectCurrentUser } from 'entities/User/User.slice';

const BASE_REQUIRED_FIELDS = ['accountName', 'lastName', 'firstName', 'email'];

interface IComponentProps {
  onSubmit: (payload: IUpdateProfilePayload) => void;
  loading: boolean;
  error: string | null;
  currentUserId?: number;
  onErrorClear: () => void;
}

export const SelfRegistrationProfile: FC<IComponentProps> = ({ onSubmit, loading, error, currentUserId, onErrorClear }) => {
  const dispatch = useAppDispatch();
  // user
  const currentUser = useAppSelector(selectCurrentUser);

  const [form] = useForm();
  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [isFormFilled, setIsFormFilled] = useState<boolean>(false);

  useEffect(() => {
    const fetch = async () => {
      if (currentUserId) {
        await dispatch(getCurrentUser(currentUserId));
      }

      setFetchLoading(false);
    };

    fetch();
  }, [currentUserId]);

  useEffect(() => {
    if (!currentUser?.email) {
      return;
    }

    form.setFieldsValue({
      accountName: currentUser.account.name,
      lastName: currentUser.lastName,
      firstName: currentUser.firstName,
      patronymic: currentUser.patronymic,
      email: currentUser.email,
    });

    setIsFormFilled(true);
  }, [currentUser]);

  const handleSubmit = (values: IUpdateProfilePayload) => {
    onErrorClear();
    onSubmit({
      ...values,
      phone: values.phone ? normalizePhone(values.phone) : undefined,
    });
  };

  const onValuesChange = () => {
    const values = form.getFieldsValue(BASE_REQUIRED_FIELDS);
    const allFilled = BASE_REQUIRED_FIELDS.every((field) => {
      const val = values[field];
      return !!val?.trim();
    });
    setIsFormFilled(allFilled);
  };

  return (
    <div className="self-registration-step">
      <div className="self-registration-step__title">Заполните профиль</div>

      {error && <Alert type="error" message={error} showIcon />}

      {fetchLoading ? null : (
        <Form
          className="self-registration-step__form"
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={handleSubmit}
          onValuesChange={onValuesChange}
        >
          <Form.Item className="w-100" label="Название организации*" name="accountName" rules={[rules.required()]}>
            <Input placeholder="Введите название организации" />
          </Form.Item>

          <Form.Item className="w-100" label="Фамилия*" name="lastName" rules={[rules.required()]}>
            <Input placeholder="Введите фамилию" />
          </Form.Item>

          <Form.Item className="w-100" label="Имя*" name="firstName" rules={[rules.required()]}>
            <Input placeholder="Введите имя" />
          </Form.Item>

          <Form.Item className="w-100" label="Отчество" name="patronymic">
            <Input placeholder="Введите отчество" />
          </Form.Item>

          <Form.Item
            className="w-100"
            label="Email*"
            name="email"
            rules={[rules.required(), rules.type('email', EFormFieldMessage.InvalidEmailFormat)]}
          >
            <Input placeholder="Введите email" />
          </Form.Item>

          <div className="self-registration-step__actions">
            <Button className="button-lg primary" loading={loading} disabled={!isFormFilled} htmlType="submit">
              Далее
            </Button>
          </div>
        </Form>
      )}
    </div>
  );
};
